
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  image: string;
  text: string;
  rating: number;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Hikmat Buda",
    role: "Founder & CEO",
    company: "Dewal School of Hotel Management",
    image: "/uploads/hikmat.png",
    text: "DG IT transformed our digital presence with their exceptional video marketing strategy. Their creative approach and technical expertise helped us reach our target audience effectively. The team is responsive, reliable, and truly understands our business needs.",
    rating: 5
  },
  {
    id: 2,
    name: "Arjun Negi ",
    role: "HOD Education",
    company: "FWU",
    image: "/uploads/arjunnegi.jpg",
    text: "Working with DG IT has been a game-changer for our college. Their logo design and 3D animation services have given our college a fresh, modern look that resonates with our social presence. Highly professional team that delivers on time and within budget.",
    rating: 5
  },
  {
    id: 3,
    name: "Dr.Bimal Chand",
    role: "Owner of Bimal Dental",
    company: "Bimal Dental",
    image: "/uploads/bimal.jpg",
    text: "The team at DG IT created a stunning, -mind bblowing video ad that perfectly represents our brand. Their attention to detail and focus on user experience has significantly improved our online conversion rates. Couldn't be happier with the results!",
    rating: 5
  },
  {
    id: 4,
    name: "Hrithik Bhatt ",
    role: "Owner of Puspanjali Suppliers",
    company: "Puspanjali Suppliers",
    image: "/uploads/hritik.jpg",
    text: "DGITs high quality video services have truly elevated our Sales . Truly best to work with them .",
    rating: 5
  }
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isAutoplay) {
      autoplayRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      }, 5000);
    }
    
    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [isAutoplay]);

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
    setIsAutoplay(false);
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <svg
        key={index}
        className={`w-5 h-5 ${index < rating ? 'text-yellow-400' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-blue-50" id="testimonials">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="section-heading">Client <span className="text-blue-500">Testimonials</span></h2>
          <p className="section-subheading">
            Hear what our clients have to say about working with us
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto relative">
          <div className="overflow-hidden">
            <motion.div
              className="flex transition-all duration-500 ease-in-out"
              animate={{ x: `-${currentIndex * 100}%` }}
              transition={{ type: "spring", stiffness: 120, damping: 20 }}
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="min-w-full px-4">
                  <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
                    <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-blue-100">
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      </div>
                      <div className="flex-grow text-center md:text-left">
                        <div className="flex justify-center md:justify-start mb-2">
                          {renderStars(testimonial.rating)}
                        </div>
                        <blockquote className="text-gray-700 italic mb-4">
                          "{testimonial.text}"
                        </blockquote>
                        <div className="mt-4">
                          <div className="font-bold text-lg">{testimonial.name}</div>
                          <div className="text-blue-600">{testimonial.role}, {testimonial.company}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 focus:outline-none ${
                  currentIndex === index ? 'bg-blue-600 w-6' : 'bg-gray-300'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          {/* Client Logos */}
          <motion.div 
            className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-8 items-center justify-items-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
